import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import AvatarComponent from "components/avatar";
import ExpandPhoto from "components/expand-photo";
import PhotoPicker from "components/photo-picker";
import { useUpdatePatientPhotoMutation } from "services/patients";

import usePatient from "../../use-patient.hook";

interface AvatarProps {
  fullName: string;
  avatar: string;
  expandPhoto: boolean;
  id?: number;
  imgSize?: number;
}

export default function Avatar({
  fullName,
  avatar,
  expandPhoto,
  imgSize,
  id
}: AvatarProps) {
  const [open, setOpen] = useState(false);
  const [openExpandPhoto, setOpenExpandPhoto] = useState(false);
  const [updatePatientPhoto, { isLoading }] = useUpdatePatientPhotoMutation();
  const { patientId = "" } = useParams();
  const { patient } = usePatient();

  const handleSubmit = useCallback(
    async (files: File[]) => {
      const formData = new FormData();
      if (files?.length > 0) {
        files?.forEach((el: File) => formData.append("photo", el));
      } else {
        formData.append("photo", "");
      }

      try {
        await updatePatientPhoto({ id: Number(patientId || id), formData });
        return;
      } catch (e) {
        return;
      }
    },
    [patientId, id, updatePatientPhoto]
  );

  const folderSettings = useMemo(() => {
    return {
      id: patient?.id,
      type: "patients" as const,
      folderId: patient?.box_folder_id,
      defaultFolderDestinationPath: `${patient?.first_name} ${patient?.last_name}/avatar`
    };
  }, [patient]);

  return (
    <>
      <AvatarComponent
        fullName={fullName}
        imgSrc={avatar}
        expandPhoto={expandPhoto}
        imgSize={imgSize}
        onChangePhoto={() => setOpen(true)}
        onExpandPhoto={() => setOpenExpandPhoto(true)}
      />
      <PhotoPicker
        open={open}
        onClose={() => setOpen(false)}
        isLoading={isLoading}
        onSubmit={handleSubmit}
        hasInitialImage={Boolean(avatar)}
        canScan
        folderSettings={folderSettings}
      />
      <ExpandPhoto
        onClose={() => setOpenExpandPhoto(false)}
        open={openExpandPhoto}
        imgSrc={avatar}
      />
    </>
  );
}
